/**
 * PushNotificationManager - Handles push notification subscription and UI
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, BellRing, Loader2, CheckCircle, XCircle } from 'lucide-react';

// Check if push notifications are supported
const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const usePushNotifications = () => {
  const { token, user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] = useState('default');
  const [registration, setRegistration] = useState(null);

  // Check support and current status on mount
  useEffect(() => {
    const checkSupport = async () => {
      if (!isPushSupported()) {
        setIsSupported(false);
        return;
      }
      
      setIsSupported(true);
      setPermission(Notification.permission);
      
      // Register service worker if not already registered
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        setRegistration(reg);
        
        // Check if already subscribed
        const subscription = await reg.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (err) {
        console.error('Service worker registration failed:', err);
      }
    };
    
    checkSupport();
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !token || !registration) return false;
    
    setIsLoading(true);
    
    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        toast.error('Notification permission denied');
        setIsLoading(false);
        return false;
      }
      
      // Get VAPID public key from server
      const { data: { publicKey } } = await axios.get(`${API}/push/vapid-public-key`);
      
      // Subscribe to push manager
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
      
      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys,
        user_agent: navigator.userAgent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsSubscribed(true);
      toast.success('Push notifications enabled! 🔔');
      return true;
      
    } catch (err) {
      console.error('Push subscription failed:', err);
      toast.error('Failed to enable push notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, token, registration]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!registration || !token) return false;
    
    setIsLoading(true);
    
    try {
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();
        
        // Notify server
        await axios.delete(`${API}/push/unsubscribe`, {
          params: { endpoint: subscription.endpoint },
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setIsSubscribed(false);
      toast.success('Push notifications disabled');
      return true;
      
    } catch (err) {
      console.error('Push unsubscription failed:', err);
      toast.error('Failed to disable push notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [registration, token]);

  // Send test notification
  const sendTest = useCallback(async () => {
    if (!token) return;
    
    try {
      await axios.post(`${API}/push/send-test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test notification sent!');
    } catch (err) {
      toast.error('Failed to send test notification');
    }
  }, [token]);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
    sendTest
  };
};

// Push notification toggle button component
export const PushNotificationToggle = ({ className = '' }) => {
  const { user } = useAuth();
  const {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
    sendTest
  } = usePushNotifications();

  if (!isSupported) {
    return null; // Don't show anything if not supported
  }

  if (!user) {
    return null; // Don't show if not logged in
  }

  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  return (
    <button
      onClick={handleToggle}
      disabled={isLoading}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
        isSubscribed 
          ? 'bg-neon-green/20 text-neon-green border border-neon-green/30 hover:bg-neon-green/30' 
          : 'bg-gray-700/50 text-gray-400 border border-gray-600 hover:bg-gray-700 hover:text-white'
      } ${className}`}
      title={isSubscribed ? 'Disable push notifications' : 'Enable push notifications'}
      data-testid="push-notification-toggle"
    >
      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isSubscribed ? (
        <BellRing className="w-4 h-4" />
      ) : (
        <BellOff className="w-4 h-4" />
      )}
      <span className="font-orbitron text-xs hidden sm:inline">
        {isSubscribed ? 'NOTIFICATIONS ON' : 'NOTIFICATIONS'}
      </span>
    </button>
  );
};

// Detailed push notification settings panel
export const PushNotificationSettings = () => {
  const { user } = useAuth();
  const {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
    sendTest
  } = usePushNotifications();

  if (!user) {
    return (
      <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
        <p className="text-gray-400 text-sm">Please log in to manage notifications</p>
      </div>
    );
  }

  if (!isSupported) {
    return (
      <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
        <div className="flex items-center gap-2 text-red-400">
          <XCircle className="w-5 h-5" />
          <span className="font-orbitron text-sm">Push notifications not supported</span>
        </div>
        <p className="text-gray-400 text-sm mt-2">
          Your browser doesn't support push notifications. Try using Chrome, Firefox, or Edge.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700 space-y-4" data-testid="push-settings">
      {/* Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isSubscribed ? 'bg-neon-green/20' : 'bg-gray-700'}`}>
            {isSubscribed ? (
              <BellRing className="w-5 h-5 text-neon-green" />
            ) : (
              <BellOff className="w-5 h-5 text-gray-400" />
            )}
          </div>
          <div>
            <h3 className="font-orbitron text-sm text-white">Push Notifications</h3>
            <p className={`text-sm ${isSubscribed ? 'text-neon-green' : 'text-gray-500'}`}>
              {isSubscribed ? 'Enabled' : 'Disabled'}
            </p>
          </div>
        </div>
        
        <button
          onClick={isSubscribed ? unsubscribe : subscribe}
          disabled={isLoading}
          className={`px-4 py-2 rounded-lg font-orbitron text-xs flex items-center gap-2 transition-colors ${
            isSubscribed 
              ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
              : 'bg-neon-blue/20 text-neon-blue hover:bg-neon-blue/30'
          }`}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isSubscribed ? (
            <>
              <BellOff className="w-4 h-4" />
              DISABLE
            </>
          ) : (
            <>
              <Bell className="w-4 h-4" />
              ENABLE
            </>
          )}
        </button>
      </div>
      
      {/* Permission status */}
      {permission === 'denied' && (
        <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
          <p className="text-yellow-400 text-sm">
            ⚠️ Notifications are blocked in your browser. Please allow notifications in your browser settings.
          </p>
        </div>
      )}
      
      {/* Test button */}
      {isSubscribed && (
        <button
          onClick={sendTest}
          className="w-full py-2 rounded-lg bg-gray-700 text-gray-300 font-rajdhani text-sm hover:bg-gray-600 transition-colors"
        >
          Send Test Notification
        </button>
      )}
      
      {/* Info */}
      <p className="text-gray-500 text-xs">
        Receive alerts for weather updates, leaderboard changes, and important announcements even when you're not on the site.
      </p>
    </div>
  );
};

export default PushNotificationToggle;
